import { BoxSubscriptionDetailsEntity } from "@modules/boxSubscription/entities/boxSubscriptionDetailsEntity"
import {
  DurationType,
  PauseInputFormik,
  pauseYupValidation,
} from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { useBoxSubscriptionActions } from "@modules/boxSubscription/hooks/useBoxSubscription"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUButton } from "@modules/ui/components/huButton"
import { HUCardAlert } from "@modules/ui/components/huCardAlert"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { LoggerType } from "@modules/utils/loggerUtils"
import dayjs from "dayjs"
import { useFormik } from "formik"
import { ArrowLeft, Info } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useTheme } from "styled-components"

type Props = {
  memberId: string
  subscription: BoxSubscriptionDetailsEntity
  setIsOfferSupensionVisible: React.Dispatch<React.SetStateAction<boolean>>
  className?: string
} & React.ComponentProps<"div">

export function MemberOfferPauseForm({
  memberId,
  subscription,
  setIsOfferSupensionVisible,
  className,
  ...props
}: Props) {
  const defaultClassName = "flex flex-column w-full pb-4"
  const theme = useTheme()
  const toast = useToast()
  const language = useLanguage()

  const [isDisableMonths, setIsDisableMonths] = useState(false)
  const [isResumeVisible, setIsResumeVisible] = useState(false)
  const { pauseSubscription, resumeSubscription, isPending } = useBoxSubscriptionActions(memberId, subscription.id)

  const suspensionTypesOptions = useMemo(() => {
    return [
      { value: DurationType.MONTHLY, label: word("box.member.table.offer.pause.monthlyInputLabel") },
      { value: DurationType.UNLIMITED, label: word("box.member.table.offer.pause.unlimitedInputLabel") },
    ]
  }, [language])

  const formik = useFormik<PauseInputFormik>({
    initialValues: { durationType: DurationType.MONTHLY, months: 1 },
    validationSchema: pauseYupValidation(),
    onSubmit: async () => {
      try {
        await pauseSubscription(formik.values)
        toast.show({
          severity: "success",
          summary: word("box.member.table.offer.pause.toastSuccess"),
        })
        setIsResumeVisible(true)
      } catch (e: any) {
        console.error(LoggerType.Service + "pause subscription", e)
        toast.show({ severity: "error", summary: word("box.member.table.offer.pause.toastError") })
      }
    },
  })

  const handleOnSubmit = async () => {
    await formik.submitForm()
  }

  const handleOnResume = async () => {
    await resumeSubscription()
      .then(() => {
        toast.show({
          severity: "success",
          summary: word("box.member.table.offer.pause.toastResumeSuccess"),
        })
        setIsResumeVisible(false)
      })
      .catch((e: any) => {
        console.error(LoggerType.Service + "resume subscription", e)
        toast.show({ severity: "error", summary: word("box.member.table.offer.pause.toastResumeError") })
      })
  }

  useEffect(() => {
    if (formik.values.durationType === DurationType.UNLIMITED) {
      setIsDisableMonths(true)
      formik.setFieldValue("months", null)
    } else {
      formik.setFieldValue("months", 1)
      setIsDisableMonths(false)
    }
  }, [formik.values.durationType])

  useEffect(() => {
    if (subscription) {
      const { durationType, months } = subscription.getPause()
      formik.setFieldValue("durationType", durationType)
      formik.setFieldValue("months", months)

      if (subscription.pause) setIsResumeVisible(true)
    }
  }, [subscription])

  const durationText = useMemo(() => {
    if (subscription.pause?.resumeAt) {
      return {
        title: word("box.member.table.offer.resume.defineDuration", {
          count: dayjs(subscription.pause.resumeAt).add(1, "day").diff(dayjs(subscription.pause.pausedAt), "months"),
        }),
        subtitle: `${dayjs(subscription.pause.pausedAt).format("ll")} ${word("global.periodInterval.to")}${" "}${dayjs(
          subscription.pause.resumeAt,
        ).format("ll")}`,
      }
    } else if (subscription.pause) {
      return {
        title: word("box.member.table.offer.resume.unlimitedDuration"),
        subtitle: `  ${word("box.member.table.offer.resume.unlimitedDurationSince")} ${dayjs(
          subscription.pause.pausedAt,
        ).format("LL")}`,
      }
    }
    return { title: "", subtitle: "" }
  }, [subscription])

  return (
    <div className={className ?? defaultClassName} {...props}>
      {/* HEADER */}
      <div className="flex flex-column px-5 py-4 gap-4">
        <div className="flex">
          <ArrowLeft
            strokeWidth={2.5}
            size={26}
            onClick={() => setIsOfferSupensionVisible(false)}
            className="cursor-pointer"
          />
          <div className="flex flex-column flex-grow-1 justify-content-center align-items-center">
            <HUText fontStyle="TS" color={theme.mainWhite}>
              {word("box.member.table.offer.pause.title")}
            </HUText>

            <HUText fontStyle="BM" color={theme.neutralColor700}>
              {subscription.offer.name}
            </HUText>
          </div>
        </div>
      </div>
      <div className="flex mt-2 mb-5">
        <HUCardAlert
          title={""}
          description={word("box.member.table.offer.pause.alert")}
          icon={<Info size={90} className="mx-2" />}
          className="flex align-items-center justify-content-center py-2"
        />
      </div>
      {isResumeVisible ? (
        // RESUME
        <div className="flex flex-column mt-4">
          <HUText fontStyle="TM" color={theme.mainWhite}>
            {durationText.title}
          </HUText>
          <HUText fontStyle="LM" color={theme.neutralColor700} className="mt-2">
            {durationText.subtitle}
          </HUText>

          <div className="flex w-full gap-2 mt-8">
            <HUButton
              type="Right_Icon"
              size="M"
              colorType="Primary"
              text={word("box.member.table.offer.pause.resumeButton")}
              onClick={() => handleOnResume()}
              className="w-full"
              disabled={isPending}
            />
          </div>
        </div>
      ) : (
        // FORMRIK
        <div className="flex flex-column">
          <FormikInput
            flex
            formikStyle={{ flexGrow: 1 }}
            label={word("box.member.table.offer.pause.supensionTypeLabel")}
            type="dropdown"
            getFieldProps={formik.getFieldProps}
            isInvalid={isFormFieldInValid("durationType", formik)}
            name="durationType"
            error={getFormErrorMessage("durationType", formik)}
            dropdownProps={{
              value: formik.values.durationType,
              options: suspensionTypesOptions,
            }}
            setFieldValue={formik.setFieldValue}
            disabled={isPending}
          />
          <FormikInput
            flex
            formikStyle={{ flexGrow: 1 }}
            type="number"
            getFieldProps={formik.getFieldProps}
            isInvalid={isFormFieldInValid("months", formik)}
            name="months"
            error={getFormErrorMessage("months", formik)}
            numberProps={{
              min: 1,
              maxFractionDigits: 0,
            }}
            setFieldValue={formik.setFieldValue}
            disabled={isDisableMonths || isPending}
          />

          <HUButton
            type="Right_Icon"
            size="M"
            colorType="Primary"
            text={word("global.save")}
            disabled={isPending}
            onClick={() => handleOnSubmit()}
            className="w-full"
          />
        </div>
      )}
    </div>
  )
}
